import {
  ExportService,
  ExportFileFormat,
  ExportContext,
  ExportRange,
  ExportTaskProcessor,
} from "app/layout/export-overlay";
import { Injectable } from "@angular/core";
import { environment as env } from 'environments/environment.prod';
import { buildQuery, CombinedFilter, page, seed } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { IPaginationResult } from "app/common/interfaces";
import { OpentItemsService } from "../open-items.service";
import { takeUntil } from "rxjs";
import { get } from "lodash-es";
import ceil from "lodash-es/ceil";
import AccountStatementsAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/account-statements/account-statements.entity";

// Propiedades localizadas para el export
export const OPEN_ITEM_PROPERTIES = {
  businessPartner: "Socio de Negocios",
  document: "Nro. Doc.",
  type: "Tipo",
  creationDate: "Fecha de Creación",
  expirationDate: "Fecha de Vencimiento",
  expiredDays: "Días Vencidos",
  paymentCondition: "Condición de Pago",
  reference: "Referencia",
  total: "Total",
  balance: "Saldo",
};

@Injectable()
export class OpenItemReportExport {
  constructor(
    private exportService: ExportService,
    private openItemsService: OpentItemsService
  ) {}

  /**
   * Calcula los días vencidos
   */
  private getExpiredDays(expirationDate: Date | string): number {
    if (!expirationDate) {
      return 0;
    }

    const today = new Date();
    const expiration = new Date(expirationDate);

    today.setHours(0, 0, 0, 0);
    expiration.setHours(0, 0, 0, 0);

    if (isNaN(expiration.getTime())) {
      return 0;
    }

    const timeDifference = today.getTime() - expiration.getTime();
    const dayDifference = timeDifference / (1000 * 3600 * 24);
    return dayDifference > 0 ? Math.floor(dayDifference) : 0;
  }

  /**
   * Obtiene la referencia combinada
   */
  private getReference(item: AccountStatementsAttributes): string {
    let reference = get(item, "reference", "").trim();
    let additionalOC = get(item, "additionalOC", "").trim();
    return [reference, additionalOC].filter(Boolean).join(" - ") || "??";
  }

  /**
   * Transforma los datos de partidas abiertas para exportación
   */
  private transformData(openItems: AccountStatementsAttributes[]) {
    return openItems?.map((item: AccountStatementsAttributes) => ({
      businessPartner: get(item, "businessPartner.name", "??"),
      document: `${get(item, 'documentType.name', '??')}-${get(item, 'document', '??')}`,
      type: get(item, "codeReason", "??"),
      creationDate: ExportTaskProcessor.types.excel.date(get(item, "creationDate", null)),
      expirationDate: ExportTaskProcessor.types.excel.date(get(item, "expirationDate", null)),
      expiredDays: String(this.getExpiredDays(get(item, "expirationDate", null))),
      paymentCondition: get(item, "paymentCondition.name", "??"),
      reference: this.getReference(item),
      total: String(get(item, "totalAmount", "0")),
      balance: String(get(item, "balance", "0")),
    })) || [];
  }

  /**
   * Exporta los datos usando el filtro guardado en el servicio
   */
  export(
    format: ExportFileFormat,
    range: ExportRange,
    totalItems: number,
    pageSize: number,
    pageIndex: number,
    exportId: string,
    exportTitle: string,
    properties: any = OPEN_ITEM_PROPERTIES
  ) {
    const totalPages = ceil(totalItems / pageSize) + 1;
    
    this.exportService
      .task({
        id: exportId,
        title: exportTitle,
        format,
        iterations: range === ExportRange.AllPages ? totalPages : 1,
        start: (ctx: ExportContext) => {
          ctx.processor.localizeItemProperties(
            exportTitle,
            properties ?? OPEN_ITEM_PROPERTIES
          );
        },
        next: (ctx: ExportContext) => {
          const currentPage = range === ExportRange.AllPages ? ctx.iteration : pageIndex;
          
          // Usar seed() y page() como en liquidation-report.export.ts
          const exportFilter = buildQuery(
            seed(this.openItemsService.currentFilter), 
            page(currentPage, pageSize)
          );

          this.openItemsService.filterForExport(exportFilter)
            .pipe(takeUntil(ctx.stopped))
            .subscribe((response) => {
              const openItems = get(response, 'data[0].data', []);
              const rows = this.transformData(openItems);
              
              rows.forEach((row) => { 
                ctx.processor.addToSection(exportTitle, [row]);
              });
              ctx.next();
            });
        },
      })
      .start();
  }

  /**
   * Verifica si se puede iniciar una tarea de exportación
   */
  canStartTask(exportId: string): boolean {
    if (this.exportService.taskIsActive(exportId)) {
      this.exportService.setVisible(true);
      return false;
    }
    return true;
  }
}